import { useEffect, useRef } from "react";

import { usePathname } from "next/navigation";

import { subscribeToToastDismiss } from "./toastDismiss";

/**
 * Applied to the toast container in `toast.tsx`, so that focus landing on a field
 * inside a toast never dismisses that toast.
 */
const TOAST_ROOT_ATTRIBUTE = "data-toast-root";

const NON_FIELD_INPUT_TYPES = new Set(["button", "submit", "reset", "image"]);

/**
 * The route each visible toast was raised on, keyed by its `hideToast` action
 * creator. This lives outside the component on purpose: sibling routes each own a
 * `layout.tsx` rendering its own `StandardPage`, so navigating between them
 * unmounts one toast renderer and mounts another while Redux still says the toast
 * is showing. A component ref would reset on that remount and re-arm against the
 * new route, leaving the toast on screen (SPR-2863).
 */
const routeWhenShown = new Map<unknown, string>();

function isFieldElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.closest(`[${TOAST_ROOT_ATTRIBUTE}]`)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) return !NON_FIELD_INPUT_TYPES.has(target.type);
  return false;
}

/**
 * Dismisses an on-screen toast on each interaction listed in SPR-2863: entering a
 * field, opening an overlay that covers the page, and navigating to another page.
 * Toast CTAs dismiss from their own click handlers instead, so that the analytics
 * they fire still see the toast that was clicked.
 */
export function useToastAutoDismiss(
  active: boolean,
  dismiss: () => void,
  { identity, persistAcrossNavigation = false }: UseToastAutoDismissOptions,
): void {
  const dismissRef = useRef(dismiss);
  // Assigned in an effect rather than during render: React may discard a render,
  // and the listeners below would then call a callback that never committed.
  useEffect(() => {
    dismissRef.current = dismiss;
  }, [dismiss]);

  const pathname = usePathname();

  useEffect(() => {
    if (!active || persistAcrossNavigation) {
      routeWhenShown.delete(identity);
      return;
    }
    const raisedOn = routeWhenShown.get(identity);
    if (raisedOn === undefined) {
      // First render of this toast — including a remount onto a new route, where
      // the entry below has already been consumed. Nothing to compare against yet.
      routeWhenShown.set(identity, pathname);
      return;
    }
    if (pathname !== raisedOn) {
      routeWhenShown.delete(identity);
      dismissRef.current();
    }
  }, [active, pathname, persistAcrossNavigation, identity]);

  useEffect(() => {
    if (!active) return;

    const handleFocusIn = (event: FocusEvent) => {
      if (isFieldElement(event.target)) {
        dismissRef.current();
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    const unsubscribe = subscribeToToastDismiss(() => dismissRef.current());

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      unsubscribe();
    };
  }, [active]);
}

export interface UseToastAutoDismissOptions {
  /**
   * Stable identity for this toast across remounts — the `hideToast` action
   * creator, which is a module-level import at every call site.
   */
  identity: unknown;
  /**
   * Keeps the toast up across a route change. Only for toasts that exist *because*
   * we are navigating the user — the session-expiry notice raised alongside the
   * redirect to /login would otherwise dismiss itself on arrival.
   */
  persistAcrossNavigation?: boolean;
}
